import React from 'react';
import { BadgeCheck, Shield } from 'lucide-react';

/**
 * Verified Coach Badge Component
 * Shows a verification badge for verified coaches
 */
export default function VerifiedBadge({ 
  isVerified = false, 
  size = 'default', // 'small', 'default', 'large'
  showLabel = false,
  className = '' 
}) {
  if (!isVerified) return null;

  const sizeClasses = {
    small: 'w-3.5 h-3.5',
    default: 'w-4 h-4',
    large: 'w-5 h-5',
  };

  const labelSizes = {
    small: 'text-xs',
    default: 'text-sm',
    large: 'text-base',
  };

  return (
    <span 
      className={`inline-flex items-center gap-1 text-blue-500 ${className}`}
      title="Coach vérifié par Motivya"
      data-testid="verified-badge"
    >
      <BadgeCheck className={`${sizeClasses[size]} fill-blue-500 text-white`} />
      {showLabel && (
        <span className={`font-medium ${labelSizes[size]}`}>Vérifié</span>
      )}
    </span>
  );
}

/**
 * Verified Badge with Background - for cards and profiles
 */
export function VerifiedBadgeCard({ isVerified = false, className = '' }) {
  if (!isVerified) return null;

  return (
    <span 
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-500 text-xs font-medium ${className}`}
      title="Coach vérifié par Motivya"
      data-testid="verified-badge-card"
    >
      <BadgeCheck className="w-3.5 h-3.5 fill-blue-500 text-white" />
      <span>Vérifié</span>
    </span>
  );
}

/**
 * Trust Indicators - Shows verification status with explanation
 */
export function TrustIndicators({ coach, className = '' }) {
  if (!coach) return null;

  const profile = coach.profile || {};
  const isVerified = coach.is_verified || profile.is_verified;
  const experienceYears = profile.experience_years || 0;
  const certifications = profile.certifications || [];

  return (
    <div className={`space-y-2 ${className}`}>
      {/* Verification Badge */}
      {isVerified && (
        <div className="flex items-center gap-2 p-2 rounded-lg bg-blue-500/10 border border-blue-500/20">
          <BadgeCheck className="w-5 h-5 fill-blue-500 text-white flex-shrink-0" />
          <div>
            <span className="text-sm font-medium text-blue-500">Coach Vérifié</span>
            <p className="text-xs text-muted-foreground">
              Identité et qualifications vérifiées par Motivya
            </p>
          </div>
        </div>
      )}

      {/* Experience */}
      {experienceYears > 0 && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Shield className="w-4 h-4 text-green-500" />
          <span>{experienceYears} ans d'expérience</span>
        </div>
      )}

      {/* Certifications */}
      {certifications.length > 0 && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <BadgeCheck className="w-4 h-4 text-orange-500" />
          <span>{certifications.length} certification{certifications.length > 1 ? 's' : ''}</span>
        </div>
      )}
    </div>
  );
}
